/*
 * DataTable
 * @module components
 * @flow
 */

import React from 'react';
import styled, { css } from 'styled-components/macro';
import { palette } from 'styled-theme';
import { ifProp, ifNotProp } from 'styled-tools';
import ReactTable from 'react-table';
import withFixedColumns from 'react-table-hoc-fixed-columns';
import * as R from 'ramda';

import 'react-table/react-table.css';
import 'react-table-hoc-fixed-columns/lib/styles.css';

import { PAGINATION } from '~/helpers';
import sortIcon from '@/icons/sort.svg';

import {
  DataTableCell,
  DataTablePagination,
  DataTableLoading,
  DataTableEmpty,
} from './components';

type DataTableColumnProps = {
  id?: string,
  Header: React$Node,
  accessor: string | ((*) => string | number),
  Cell?: React$Node,
  fixed?: 'left' | 'right',
};

export type DataTableProps = {
  title?: React$Node,
  columns: DataTableColumnProps[],
  data: *[],
  pageSize?: number,
  palette?: string,
  search?: React$Node,
  highlighted?: boolean,
  onReset?: ?() => void,
  onScroll?: ?(SyntheticEvent<*>) => void,
  isScrolled?: boolean,
  sortBy?: string,
  hidePagination?: boolean,
  device?: $PropertyType<DeviceQuery, 'device'>,
  orientation?: $PropertyType<DeviceQuery, 'orientation'>,
  fixedFirstColumns?: boolean | string[],
  loading?: boolean,
  headHeight?: number,
  rowHeight?: number,
};

const Table = withFixedColumns(ReactTable);

const DataTableWrapper = styled.div.attrs(() => ({
  className: 'screen-swiper-disabled',
  testid: 'datatable',
}))`
  display: flex;
  flex-direction: column;
  min-height: 280px;
  flex: 1;

  .ReactTable {
    min-height: 0;
    flex: 1;
    border: 0 none;
    color: ${palette('grayscale', 1)};

    &.rthfc.-sp {
      .rt-tbody {
        flex: 1;
      }
    }

    & .rt-table {
      z-index: 1;

      ${ifNotProp(
        { device: 'desktop' },
        css`
          min-height: ${({ minTableHeight }) => minTableHeight}px;
        `,
      )}

      .rt-thead.-headerGroups {
        display: none;
      }
    }
  }
`;

const DataTableTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: ${palette('grayscale', 0)};
`;

const DataTableHead = styled.div.attrs(() => ({
  className: 'rt-thead',
}))`
  position: sticky;
  top: 0;

  &&&&& {
    flex-grow: 0;
    box-shadow: ${ifProp(
      { isScrolled: true },
      '0 12px 12px -6px rgba(176,192,237,0.42)',
      'none',
    )};
    ${ifProp(
      { highlighted: true },
      css`
        background: #ecf1f5;
      `,
    )};
  }
`;

const DataTableRowGroup = styled.div.attrs(() => ({
  role: 'rowgroup',
  className: 'rt-tr-group',
}))`
  &&&&& {
    flex-grow: 0;
    border-bottom: 1px solid #f3f3f3;
  }
`;

const DataTableRow = styled.div.attrs(() => ({
  role: 'row',
  className: 'rt-tr',
}))`
  &&&&& {
    flex-grow: 0;
    border: 0 none;
  }

  .rt-th:first-child {
    margin-left: 0 !important;
  }

  .rt-th:last-child {
    margin-right: 0 !important;
  }

  .rt-td:first-child {
    margin-left: 0 !important;
  }

  .rt-td:last-child {
    margin-right: 0 !important;
  }
`;

const DataTableColumnCell = styled.div.attrs(() => ({
  role: 'gridcell',
  className: 'rt-td',
}))`
  &&&&& {
    display: inline-flex;
    margin: 0 8px;
    align-items: center;
    border: 0 none;
    justify-content: center;
    line-height: ${ifProp({ device: 'mobile' }, '20px', '33px')};

    &.dataTable-alignLeft {
      justify-content: initial;
    }

    &.dataTable-noWrap {
      white-space: normal;
    }
  }
`;

const DataTableColumnHeader = styled(DataTableColumnCell).attrs(
  ({ toggleSort, isCurrentSort }) => ({
    role: 'columnheader',
    className: 'rt-th',
    onClick: !isCurrentSort ? toggleSort : undefined,
  }),
)`
  &&&&& {
    padding-top: 2px;
    padding-bottom: 2px;
    font-weight: 600;
    line-height: 1;
    box-shadow: none;
    overflow: visible;

    .rt-resizable-header-content {
      white-space: normal;
    }

    ${ifProp(
      { highlighted: true },
      css`
        background: #ecf1f5;
      `,
    )};

    ${ifProp(
      { sortable: true },
      css`
        .rt-resizable-header-content {
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: center;
          overflow: inherit;

          &::before {
            content: '';
            margin-right: 6px;
            background: url(${sortIcon}) 50% 50% no-repeat transparent;
            width: 12px;
            height: 9px;
            opacity: 0.3;
          }
        }

        &.-sort-asc {
          .rt-resizable-header-content {
            &::before {
              opacity: 0.9;
            }
          }
        }

        &.dataTable-alignLeft {
          .rt-resizable-header-content {
            justify-content: flex-start;
          }
        }
      `,
    )};
  }
`;

const DataTableHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 27px;
`;

const DataTableSearch = styled.div`
  margin: -30px 0 -10px;
`;

const DataTableTableWrapper = styled.div`
  flex: 1;
  position: relative;

  & > div {
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;

    ${ifProp(
      { device: 'desktop' },
      css`
        overflow: hidden;
        overflow-x: auto;
      `,
    )}
  }
`;

const getSorted = (sortBy) => {
  if (!sortBy) {
    return [];
  }

  return [{ id: sortBy, desc: false }];
};

const prepareColumns = R.map((item) =>
  item.Cell
    ? item
    : {
        ...item,
        Cell: ({ value }) => {
          const handledValue = item.valueHandler
            ? item.valueHandler(value)
            : value;

          return item.withTooltip ? (
            <DataTableCell value={handledValue} />
          ) : (
            handledValue
          );
        },
      },
);

/**
 * DataTable React-component
 * @memberof components
 * @param {Object} props - properties
 * @param {React.Node<*>} [props.title] - title of the table
 * @param {Array<*>} [props.data = []] - data passed to the table
 * @param {DataTableColumnProps[]} [props.columns = []] - columns
 * @param {number[]} [props.pageSizeOptions = [20, 40, 60, 80, 100]] - list of available page sizes
 * @param {number} [props.defaultPageSize = 20] - items per page by default
 * @param {boolean} [props.highlighted] - highlighted head of table
 * @param {Function} [props.onReset] - on filter reset handler
 * @param {Function} [props.onScroll] - on body table scroll handler
 * @param {string} [props.palette = 'primary'] - current palette
 * @param {boolean} [props.hidePagination] - don't show pagination flag
 * @param {number} [props.headHeight] - table head height
 * @param {number} [props.rowHeight] - table row height
 * @example <caption>Simple usage</caption>
 * <DataTable title="Transaction History" />
 * @returns {React.Node<*>} React-component
 */
export const DataTable = ({
  title,
  search,
  highlighted,
  onReset,
  sortBy,
  onScroll,
  isScrolled,
  hidePagination,
  columns,
  fixedFirstColumns,
  device,
  orientation,
  data,
  loading,
  headHeight,
  rowHeight,
  ...rest
}: DataTableProps) => {
  let dataTableColumns = prepareColumns(columns);
  const dataTableColumnsSize = R.length(dataTableColumns);
  const customComponents = {
    TrGroupComponent: ({ ...v }) => <DataTableRowGroup {...v} />,
    TrComponent: ({ ...v }) => <DataTableRow {...v} />,
    TheadComponent: ({ ...v }) => <DataTableHead {...v} />,
    ThComponent: ({ ...v }) => <DataTableColumnHeader {...v} />,
    TdComponent: ({ ...v }) => <DataTableColumnCell device={device} {...v} />,
    PaginationComponent: ({ ...v }) => <DataTablePagination {...v} />,
    LoadingComponent: ({ ...v }) => <DataTableLoading {...v} />,
    NoDataComponent: ({ ...v }) => <DataTableEmpty {...v} />,
  };

  if (fixedFirstColumns && dataTableColumnsSize) {
    if (Array.isArray(fixedFirstColumns)) {
      const includedColumn = R.pipe(
        R.prop('accessor'),
        // $FlowIssue
        R.includes(R.__, fixedFirstColumns), // eslint-disable-line no-underscore-dangle
      );

      dataTableColumns = R.map(
        R.cond([
          [includedColumn, R.assoc('fixed', 'left')],
          [R.T, R.identity],
        ]),
        dataTableColumns,
      );
    } else {
      const isMobilePortrait =
        device !== 'desktop' && orientation === 'portrait';

      dataTableColumns[0].fixed = 'left';

      if (!isMobilePortrait && dataTableColumnsSize > 2) {
        dataTableColumns[1].fixed = 'left';
      }
    }
  }

  const minTableHeight = loading ? 0 : R.length(data) * rowHeight + headHeight;

  return (
    <DataTableWrapper
      testid="datatable"
      minTableHeight={minTableHeight}
      device={device}
    >
      {(title || search) && (
        <DataTableHeader>
          {title && <DataTableTitle>{title}</DataTableTitle>}
          {search && <DataTableSearch>{search}</DataTableSearch>}
        </DataTableHeader>
      )}
      <DataTableTableWrapper device={device}>
        <Table
          getTheadProps={() => ({ highlighted, isScrolled })}
          getTheadThProps={(
            { sortable: sortableTable, sorted },
            _,
            { id, sortable: sortableCell },
          ) => {
            const isCurrentSort = R.pipe(
              R.path([0, 'id']),
              R.equals(id),
            )(sorted);

            return {
              sortable: sortableTable !== false && sortableCell !== false,
              highlighted,
              isCurrentSort,
            };
          }}
          getTbodyProps={() => ({ onScroll })}
          getLoadingProps={() => ({ highlighted, loading })}
          getNoDataProps={() => ({ highlighted, onReset, loading })}
          getPaginationProps={() => ({
            hidePagination: hidePagination || R.isEmpty(data),
            device,
          })}
          sorted={getSorted(sortBy)}
          columns={dataTableColumns}
          data={loading ? [] : data}
          loading={loading}
          manual
          resizable={rest.resizable}
          {...customComponents}
          {...rest}
        />
      </DataTableTableWrapper>
    </DataTableWrapper>
  );
};

DataTable.defaultProps = {
  data: [],
  columns: [],
  multiSort: false,
  minRows: 0,
  pageSizeOptions: [20, 40, 60, 80, 100],
  defaultPageSize: PAGINATION.PAGE_SIZE,
  headHeight: 36,
  rowHeight: 55,
  palette: 'primary',
};
